'use client'
import { PreviewBoutiqueGridBlock } from '@/payload-types'
import RichText from '@onclicks/sharedcomponents/RichText'
import { CMSLink } from '../../CMSLink'
import { useFetchProducts } from '@onclicks/sharedmedusa/hooks/useFetchProducts'
import { useEffect, useMemo, useState } from 'react'
import { HttpTypes } from '@medusajs/types'
import Card from './Card/Card'
import SubCategoryMenu from './SubCategoryMenu'
import Pagination from './Pagination'
import { getCategoryIdByHandle, getSubCategories } from '@onclicks/sharedmedusa/medusaData/categories'
import { cn } from '@/utilities/ui'
import { bagelFatOne, quicksand } from '@/utilities/fontImport'
import { useSearchParams } from 'next/navigation'

const ProductCardsMediumGrid: React.FC<PreviewBoutiqueGridBlock> = (data) => {
  const {
    title,
    richText,
    links,
    categoryHandle: defaultCategoryHandle,
    showSubCategoryMenu,
    onlyPromotions,
  } = data
  const searchParams = useSearchParams()
  const categoryHandle = searchParams.get('category') || defaultCategoryHandle
  const { products, loading } = useFetchProducts({
    countryCode: process.env.NEXT_PUBLIC_MEDUSA_REGION ?? 'be',
    categoryHandle,
    limit: 100,
    fetchAllPages: true,
    onlyPromotions: !!onlyPromotions,
  })
  const [subCategories, setSubCategories] = useState<HttpTypes.StoreProductCategory[]>([])
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState<string | null>(null)

  const productsPerPage = 9

  const filteredProducts = useMemo(
    () =>
      selectedSubCategoryId
        ? products.filter((product) =>
            product.categories?.some((category) => category.id === selectedSubCategoryId),
          )
        : products,
    [products, selectedSubCategoryId],
  )

  const [currentPage, setCurrentPage] = useState(1)
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage)
  const currentProducts = filteredProducts.slice(
    (currentPage - 1) * productsPerPage,
    currentPage * productsPerPage,
  )

  useEffect(() => {
    setCurrentPage(1)
  }, [selectedSubCategoryId])

  useEffect(() => {
    setSelectedSubCategoryId(null)
    setSubCategories([])
  }, [categoryHandle])

  useEffect(() => {
    if (!showSubCategoryMenu || !categoryHandle) {
      return
    }

    let cancelled = false

    const fetchSubCategories = async () => {
      const categoryId = await getCategoryIdByHandle(categoryHandle)
      if (!categoryId) {
        return
      }

      const subs = await getSubCategories(categoryId)
      if (!cancelled) {
        setSubCategories(subs)
      }
    }

    fetchSubCategories()

    return () => {
      cancelled = true
    }
  }, [categoryHandle, showSubCategoryMenu])

  return (
    <section id="products" className="px-6 lg:px-34 py-10">
      {title && (
        <h1 className={`text-primary text-4xl font-bold mb-6 ${bagelFatOne.className}`}>{title}</h1>
      )}
      {richText && (
        <RichText
          data={richText}
          enableGutter={false}
          className={`mb-4 mt-2 text-lg ${quicksand.className}`}
        />
      )}
      <div
        className={cn(
          'flex flex-col lg:flex-row items-start lg:gap-x-10',
          showSubCategoryMenu && subCategories.length > 0 && '-ml-6 lg:-ml-34',
        )}
      >
        {showSubCategoryMenu && (
          <SubCategoryMenu
            subCategories={subCategories}
            selectedSubCategoryId={selectedSubCategoryId}
            onSelect={setSelectedSubCategoryId}
          />
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 mx-3 lg:mx-auto my-8 w-full gap-6">
          {loading
            ? [1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
                <div
                  key={i}
                  className="animate-pulse bg-gray-300 h-48 rounded-2xl mb-5 flex justify-center items-center"
                />
              ))
            : currentProducts.map((product, index) => <Card key={index} product={product} />)}
        </div>
      </div>

      {!loading && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}

      <div className=" max-w-2xl lg:shrink-0 lg:text-left">
        {links && links.map((link, index) => <CMSLink key={index} {...link.link} />)}
      </div>
    </section>
  )
}

export default ProductCardsMediumGrid
